import { ProductEnumType, ResponseNativeCard, ResponseStoreFrontSmeLoan } from '@/src/interfaces/data-contracts';
import classNames from 'classnames';
import React from 'react';
import { BluePercent, HangiKrediItemIcon, PinIcon, StarIcon, Target } from '../../ui/icons/icons';

type BaseProps = {
  children: React.ReactNode;
  className?: string;
};

type SmeLoanProps = BaseProps & {
  isNativeCard?: false;
  item?: (ResponseStoreFrontSmeLoan & { order?: number }) | ResponseNativeCard;
};

type NativeCardProps = BaseProps & {
  isNativeCard: true;
  item: ResponseNativeCard;
};

type Props = SmeLoanProps | NativeCardProps;

const Badge: React.FC<{
  text: string | React.ReactNode;
  icon: React.ReactNode;
  className: string;
  noIconGap?: boolean;
}> = ({ text, icon, className, noIconGap }) => (
  <div
    className={classNames(
      'absolute left-px top-px flex h-[32px] items-center',
      { 'gap-2': !noIconGap },
      'rounded-br-xl rounded-tl-xl px-4 text-xs font-bold',
      className
    )}
    data-testid="badge"
  >
    {icon}
    {text}
  </div>
);

const mapProductTypeToEnum = {
  0: ProductEnumType.Month,
  1: ProductEnumType.Sponsor,
  2: ProductEnumType.Normal,
  3: ProductEnumType.SpecialRate,
  4: ProductEnumType.CustomizableProductWithFrame,
  5: ProductEnumType.CustomizableProductWithoutFrame,
  6: ProductEnumType.LowestInterestRate,
} as const;

export const Container: React.FC<Props> = ({ children, className, item, isNativeCard, ...rest }) => {
  let badge = null;

  if (isNativeCard) {
    return (
      <div className={classNames('relative', className)} {...rest}>
        {children}
      </div>
    );
  }

  const productTypeEnum = mapProductTypeToEnum[item?.productType as unknown as keyof typeof mapProductTypeToEnum];

  if (item && 'sponsorProduct' in item && item.sponsorProduct) {
    badge = (
      <Badge
        text={item.productTypeText ?? 'Sponsor Banka'}
        icon={<StarIcon width={16} height={16} />}
        className="bg-warningLight text-warning"
      />
    );
  } else if (item && 'isLowestInterestRate' in item && item?.isLowestInterestRate) {
    badge = (
      <Badge
        text={item.productTypeText ?? 'En Düşük Faiz'}
        icon={<BluePercent width={16} height={16} />}
        className="bg-blue100 text-intro"
      />
    );
  } else if (
    item &&
    'isSpecialProduct' in item &&
    item.isSpecialProduct &&
    productTypeEnum !== ProductEnumType.SpecialRate
  ) {
    badge = (
      <Badge
        text={"'ye Özel"}
        icon={<HangiKrediItemIcon width={86} height={15} />}
        className="bg-purple100 text-purple500"
        noIconGap
      />
    );
  } else if (productTypeEnum === ProductEnumType.SpecialRate) {
    badge = (
      <Badge
        text={item?.productTypeText ?? 'Özel Oran'}
        icon={<PinIcon width={16} height={16} />}
        className="bg-purple100 text-purple500"
      />
    );
  } else if (item && 'monthProduct' in item && item.monthProduct) {
    badge = (
      <Badge
        text={item.productTypeText ?? 'Ayın Ürünü'}
        icon={<Target width={16} height={16} />}
        className="bg-green100 text-green600"
      />
    );
  }

  return (
    <div
      className={classNames(
        'relative',
        { 'rounded-xl bg-white px-4 py-5 shadow-campCard md:px-5 md:py-10': !isNativeCard },
        className,
        [item && 'sponsorProduct' in item && item.sponsorProduct && 'gradientYellowBorderBg pt-[42px]'],
        [item && 'isLowestInterestRate' in item && item.isLowestInterestRate && 'gradientBlueBorderBg pt-[42px]'],
        [item && 'isSpecialProduct' in item && item.isSpecialProduct && 'gradientPurpleBorderBg pt-[42px]'],
        [
          !(item && 'isSpecialProduct' in item) &&
            productTypeEnum === ProductEnumType.SpecialRate &&
            'gradientPurpleBorderBg pt-[42px]',
        ],
        [item && 'monthProduct' in item && item.monthProduct && 'gradientYellowBorderBg pt-[42px]']
      )}
      {...rest}
    >
      {badge}
      {children}
    </div>
  );
};
